import { useAuth, useUser } from '@clerk/clerk-react'
import Intercom from '@intercom/messenger-js-sdk'
import { selectOrganization } from '@saaga/shared/store/slices/auth.slice'
import { selectTenant } from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useEffect, useRef } from 'react'

import { buildIntercomConfig, fetchIntercomUserHash } from './use-intercom.helper'

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID

const useIntercomInit = (isInitializedRef: React.RefObject<boolean>): void => {
  const { getToken, orgId } = useAuth()
  const { user } = useUser()
  const organization = useAppSelector(selectOrganization)
  const tenant = useAppSelector(selectTenant)

  useEffect(() => {
    if (!INTERCOM_APP_ID || !user || !orgId || isInitializedRef.current) return

    const init = async () => {
      const token = await getToken()
      if (!token) return

      const userHash = await fetchIntercomUserHash(token, orgId, user.id)
      if (!userHash) return

      const userData = {
        userId: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        createdAt: user.createdAt,
      }

      const config = buildIntercomConfig(INTERCOM_APP_ID, userData, userHash, organization, tenant)
      Intercom(config)

      isInitializedRef.current = true
      logger.debug('[Intercom] Initialized for user', user.id)
    }

    init()
  }, [user, orgId, getToken, organization, tenant, isInitializedRef])
}

const useIntercomUpdates = (isInitializedRef: React.RefObject<boolean>): void => {
  const organization = useAppSelector(selectOrganization)
  const tenant = useAppSelector(selectTenant)

  useEffect(() => {
    if (!isInitializedRef.current || !tenant) return

    window.Intercom?.('update', {
      subscription_plan: tenant.subscriptionName ?? undefined,
      subscription_status: tenant.subscriptionStatus ?? undefined,
    })
  }, [tenant?.subscriptionName, tenant?.subscriptionStatus, isInitializedRef])

  useEffect(() => {
    if (!isInitializedRef.current || !organization) return

    window.Intercom?.('update', {
      company: { company_id: organization.id, name: organization.name },
    })
  }, [organization?.id, organization?.name, isInitializedRef])
}

export const useIntercom = (): void => {
  const isInitializedRef = useRef(false)

  useIntercomInit(isInitializedRef)
  useIntercomUpdates(isInitializedRef)

  useEffect(() => {
    return () => {
      if (isInitializedRef.current) {
        window.Intercom?.('shutdown')
        isInitializedRef.current = false
      }
    }
  }, [])
}
